import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BillingRecord, CreateBillingRecordRequest, UpdateBillingRecordRequest,
  ApartmentBillingInfo,
  adminGetBillingRecordsByManager, adminCreateBillingRecord,
  adminUpdateBillingRecord, adminDeleteBillingRecord,
  adminSuspendManager, adminActivateManager,
  adminGetBillingConfig, adminGetManagerApartmentsBilling,
} from '../api.billing';
import { getAllManagers, ManagerAdmin } from '../api.admin';

const getToken = () => localStorage.getItem('token') || '';

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  PAID:    { bg: '#f0fff4', color: '#276749' },
  PENDING: { bg: '#fffbeb', color: '#92400e' },
  OVERDUE: { bg: '#fff5f5', color: '#c53030' },
};

const fmtCZK = (n: number) => `${n.toLocaleString('cs-CZ')} CZK`;
const monthName = (y: number, m: number) => new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

interface FormState {
  periodYear: number;
  periodMonth: number;
  apartmentCount: number;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  status: 'PENDING' | 'PAID' | 'OVERDUE';
  notes: string;
}

const defaultForm = (): FormState => {
  const now = new Date();
  return {
    periodYear: now.getFullYear(),
    periodMonth: now.getMonth() + 1,
    apartmentCount: 1,
    billingCycle: 'MONTHLY',
    status: 'PENDING',
    notes: '',
  };
};

const AdminBillingDetail: React.FC = () => {
  const { managerId } = useParams<{ managerId: string }>();
  const mid = Number(managerId);

  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [apartments, setApartments] = useState<ApartmentBillingInfo[]>([]);
  const [managerInfo, setManagerInfo] = useState<ManagerAdmin | null>(null);
  const [billingStatus, setBillingStatus] = useState<string>('ACTIVE');
  const [defaultPrice, setDefaultPrice] = useState(250);
  const [annualPrice, setAnnualPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(defaultForm());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [previewPrice, setPreviewPrice] = useState(defaultPrice);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const token = getToken();
    try {
      const [r, m, cfg, apts] = await Promise.all([
        adminGetBillingRecordsByManager(mid, token),
        getAllManagers(token),
        adminGetBillingConfig(token),
        adminGetManagerApartmentsBilling(mid, token),
      ]);
      setRecords(r);
      setApartments(apts);
      setDefaultPrice(cfg.pricePerApartmentMonthly);
      setAnnualPrice(cfg.pricePerApartmentAnnual);
      const found = m.find(x => x.id === mid);
      if (found) {
        setManagerInfo(found);
        setBillingStatus(found.billingStatus || 'ACTIVE');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [mid]);

  const openCreate = () => {
    setEditingId(null);
    const billableCount = apartments.filter(a => a.isBillableThisMonth).length;
    setForm({ ...defaultForm(), apartmentCount: billableCount || 1 });
    setPreviewPrice(defaultPrice);
    setShowForm(true);
    setActionError(null);
  };

  const openEdit = (r: BillingRecord) => {
    setEditingId(r.id);
    setForm({
      periodYear: r.periodYear,
      periodMonth: r.periodMonth,
      apartmentCount: r.apartmentCount,
      billingCycle: r.billingCycle,
      status: r.status,
      notes: r.notes || '',
    });
    setPreviewPrice(r.billingCycle === 'ANNUAL' ? annualPrice : defaultPrice);
    setShowForm(true);
    setActionError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setActionError(null);
    try {
      if (editingId !== null) {
        const payload: UpdateBillingRecordRequest = {
          status: form.status,
          apartmentCount: form.apartmentCount,
          notes: form.notes,
        };
        const updated = await adminUpdateBillingRecord(editingId, payload, getToken());
        setRecords(prev => prev.map(r => r.id === editingId ? updated : r));
      } else {
        const payload: CreateBillingRecordRequest = {
          managerId: mid,
          periodYear: form.periodYear,
          periodMonth: form.periodMonth,
          apartmentCount: form.apartmentCount,
          billingCycle: form.billingCycle,
          status: form.status,
          notes: form.notes,
        };
        const created = await adminCreateBillingRecord(payload, getToken());
        setRecords(prev => [created, ...prev]);
      }
      setShowForm(false);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this record?')) return;
    setActionError(null);
    try {
      await adminDeleteBillingRecord(id, getToken());
      setRecords(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleSuspend = async () => {
    setActionError(null);
    try {
      await adminSuspendManager(mid, getToken());
      setBillingStatus('SUSPENDED');
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleActivate = async () => {
    setActionError(null);
    try {
      await adminActivateManager(mid, getToken());
      setBillingStatus('ACTIVE');
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading...</div>;
  if (error) return <div style={{ padding: 16, color: '#c53030', background: '#fff5f5', borderRadius: 8 }}>{error}</div>;

  const managerName = managerInfo
    ? [managerInfo.user.firstName, managerInfo.user.lastName].filter(Boolean).join(' ') || managerInfo.user.email
    : `User ${mid}`;

  const totalPaid = records.filter(r => r.status === 'PAID').reduce((s, r) => s + r.totalAmount, 0);
  const totalPending = records.filter(r => r.status === 'PENDING').reduce((s, r) => s + r.totalAmount, 0);
  const totalOverdue = records.filter(r => r.status === 'OVERDUE').reduce((s, r) => s + r.totalAmount, 0);

  return (
    <div className="page-container">
      <div style={{ marginBottom: 4 }}>
        <Link to="/admin/billing" style={{ color: '#718096', fontSize: '0.85rem', textDecoration: 'none' }}>← Back to Users</Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 2, color: '#1a202c' }}>{managerName}</h1>
          <p style={{ color: '#718096', fontSize: '0.9rem' }}>{managerInfo?.user.email}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 12, fontSize: '0.82rem', fontWeight: 700,
            background: billingStatus === 'ACTIVE' ? '#f0fff4' : '#fff5f5',
            color: billingStatus === 'ACTIVE' ? '#276749' : '#c53030',
          }}>{billingStatus}</span>
          {billingStatus === 'ACTIVE'
            ? <button onClick={handleSuspend} style={dangerBtn}>Suspend</button>
            : <button onClick={handleActivate} style={successBtn}>Activate</button>
          }
        </div>
      </div>

      {actionError && (
        <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, padding: '10px 14px', color: '#c53030', marginBottom: 16 }}>
          {actionError}
        </div>
      )}

      {/* Revenue summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <SumCard label="Paid" value={fmtCZK(totalPaid)} color="#276749" />
        <SumCard label="Pending" value={fmtCZK(totalPending)} color="#92400e" />
        <SumCard label="Overdue" value={fmtCZK(totalOverdue)} color="#c53030" />
        <SumCard label="Total Records" value={records.length} color="#4a5568" />
      </div>

      {/* Apartments breakdown */}
      {apartments.length > 0 && (
        <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', marginBottom: 14 }}>
            Apartments
            <span style={{ fontWeight: 400, fontSize: '0.85rem', color: '#718096', marginLeft: 10 }}>
              {apartments.filter(a => a.isBillableThisMonth).length} billable this month
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={th}>Apartment</th>
                  <th style={th}>Billing Start</th>
                  <th style={th}>Monthly Price</th>
                  <th style={th}>Status</th>
                  <th style={th}>This Month</th>
                </tr>
              </thead>
              <tbody>
                {apartments.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f7fafc' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{a.name}</div>
                      {a.address && <div style={{ fontSize: '0.78rem', color: '#718096' }}>{a.address}</div>}
                    </td>
                    <td style={td}>{a.billingStartDate || '—'}</td>
                    <td style={td}>{fmtCZK(a.monthlyPrice)}</td>
                    <td style={td}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600,
                        background: a.isActive ? '#f0fff4' : '#f7fafc',
                        color: a.isActive ? '#276749' : '#a0aec0',
                      }}>{a.isActive ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td style={td}>
                      {a.isBillableThisMonth
                        ? <span style={{ color: '#276749', fontWeight: 600 }}>✓ Billable</span>
                        : <span style={{ color: '#a0aec0' }}>{!a.isActive ? 'Inactive' : 'Pending start'}</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Records table */}
      <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c' }}>Billing Records</div>
          <button onClick={openCreate} style={primaryBtn}>+ Add Record</button>
        </div>

        {/* Inline form */}
        {showForm && (
          <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: '#2d3748' }}>{editingId ? 'Edit Record' : 'New Record'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
              <Field label="Year">
                <input type="number" value={form.periodYear} onChange={e => setForm(f => ({ ...f, periodYear: Number(e.target.value) }))} style={inputStyle} />
              </Field>
              <Field label="Month">
                <input type="number" min={1} max={12} value={form.periodMonth} onChange={e => setForm(f => ({ ...f, periodMonth: Number(e.target.value) }))} style={inputStyle} />
              </Field>
              <Field label="# Apartments">
                <input type="number" min={1} value={form.apartmentCount} onChange={e => setForm(f => ({ ...f, apartmentCount: Number(e.target.value) }))} style={inputStyle} />
              </Field>
              <Field label="Price/Apt (CZK)">
                <input type="text" value={fmtCZK(previewPrice)} readOnly style={{ ...inputStyle, background: '#f7fafc', color: '#718096', cursor: 'default' }} />
              </Field>
              <Field label="Cycle">
                <select
                  value={form.billingCycle}
                  onChange={e => {
                    const cycle = e.target.value as 'MONTHLY' | 'ANNUAL';
                    setForm(f => ({ ...f, billingCycle: cycle }));
                    setPreviewPrice(cycle === 'ANNUAL' ? annualPrice : defaultPrice);
                  }}
                  style={inputStyle}
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="ANNUAL">Annual</option>
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as any }))} style={inputStyle}>
                  <option value="PENDING">Pending</option>
                  <option value="PAID">Paid</option>
                  <option value="OVERDUE">Overdue</option>
                </select>
              </Field>
            </div>
            <Field label="Notes" style={{ marginTop: 10 }}>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, width: '100%' }} placeholder="Optional notes" />
            </Field>
            <div style={{ marginTop: 6, fontSize: '0.82rem', color: '#718096' }}>
              Total: <strong>{fmtCZK(form.apartmentCount * previewPrice)}</strong>
              <span style={{ marginLeft: 8, color: '#a0aec0' }}>(calculated by server from config)</span>
            </div>
            {actionError && <div style={{ color: '#c53030', fontSize: '0.85rem', marginTop: 8 }}>{actionError}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={handleSave} disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setShowForm(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={th}>Period</th>
                <th style={th}>Apts</th>
                <th style={th}>Price/Apt</th>
                <th style={th}>Total</th>
                <th style={th}>Cycle</th>
                <th style={th}>Status</th>
                <th style={th}>Notes</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => {
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.PENDING;
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f7fafc' }}>
                    <td style={td}>{monthName(r.periodYear, r.periodMonth)}</td>
                    <td style={td}>{r.apartmentCount}</td>
                    <td style={td}>{fmtCZK(r.pricePerApartment)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{fmtCZK(r.totalAmount)}</td>
                    <td style={td}>{r.billingCycle}</td>
                    <td style={td}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, background: sc.bg, color: sc.color }}>{r.status}</span>
                    </td>
                    <td style={{ ...td, color: '#718096' }}>{r.notes || '—'}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(r)} style={editBtn}>Edit</button>
                        <button onClick={() => handleDelete(r.id)} style={dangerBtn}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {records.length === 0 && <div style={{ color: '#a0aec0', padding: '16px 0', textAlign: 'center' }}>No records yet</div>}
        </div>
      </section>
    </div>
  );
};

const SumCard: React.FC<{ label: string; value: string | number; color: string }> = ({ label, value, color }) => (
  <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
    <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>{value}</div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; style?: React.CSSProperties }> = ({ label, children, style }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, ...style }}>
    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4a5568' }}>{label}</span>
    {children}
  </label>
);

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: '#4a5568', fontSize: '0.8rem' };
const td: React.CSSProperties = { padding: '10px 10px', color: '#2d3748', verticalAlign: 'middle' };
const inputStyle: React.CSSProperties = { border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: '0.875rem' };
const primaryBtn: React.CSSProperties = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 };
const cancelBtn: React.CSSProperties = { background: '#f7fafc', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: 6, padding: '7px 14px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 };
const dangerBtn: React.CSSProperties = { background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 };
const successBtn: React.CSSProperties = { background: '#f0fff4', color: '#276749', border: '1px solid #9ae6b4', borderRadius: 6, padding: '6px 14px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 };
const editBtn: React.CSSProperties = { background: '#ebf4ff', color: '#2563eb', border: '1px solid #bee3f8', borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 };

export default AdminBillingDetail;
